import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"
import LinksFieldset from "../../links/pages/LinksFieldset"
import type { LinkRow, LinksFieldsetLabels } from "../../links/pages/LinksFieldset"

interface AdminTripFormLabels {
  heading: string
  back: string
  titleLabel: string
  titleHint: string
  startDateLabel: string
  endDateLabel: string
  datesHint: string
  timezoneLabel: string
  timezoneHint: string
  memberCategoryLabel: string
  memberCategoryHint: string
  optional: string
  linksHeading: string
  linksRowHeading: string
  linksRemove: string
  linksAdd: string
  linksLabelLabel: string
  linksUrlLabel: string
  linksHint: string
  save: string
}

interface AdminTripFormProps {
  trip: {
    title: string
    startDate: string
    endDate: string
    timezone: string
    memberCategory: string
    links: LinkRow[]
  }
  // t014, FR-4: Trip::SuggestTimezones's own filtered, sorted identifier
  // list -- the timezone field's <datalist> source, same shape as the
  // contact form's categoryOptions.
  timezoneOptions: string[]
  // t017, FR-9.2: Contacts::SuggestCategories's own datalist source --
  // the categories already in use across contacts, suggesting only, not
  // constraining (FR-9.3).
  memberCategoryOptions: string[]
  labels: AdminTripFormLabels
}

// The form's own local state, kept flat (not nested under "trip") on
// purpose: Trip::AdminTripController's failed-save redirect carries
// `errors` keyed by the model's own bare attribute names (title,
// start_date, ...), and useForm's `errors` object is only ever populated
// from that same top-level Inertia shared prop, key-for-key, with no path
// translation. A flat TForm keeps every `form.errors.<field>` read below
// aligned with the server's own keys (plan: "Error keys are the model's
// snake_case attribute names").
interface TripFormData {
  title: string
  start_date: string
  end_date: string
  timezone: string
  member_category: string
  links: LinkRow[]
  authenticity_token: string
}

const TIMEZONE_LIST_ID = "trip-timezones"
const MEMBER_CATEGORY_LIST_ID = "trip-member-categories"

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const DATE_INPUT_CLASSES = `${INPUT_CLASSES} tabular-nums`
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The trip form (docs/design/admin.html State 4, implemented verbatim for
// title/start/end; the time zone field is new -- see config/locales/
// {en,ru}.yml's admin.trip comment for why FR-8/AC-7 add it though the
// design does not draw it). FR-9..FR-12's reconciliation and narrowing
// refusal are server-side (Trip::SaveTrip); this page only renders the
// five fields and whichever per-field error round-trips back (FR-21).
// The timezone and member-category fields also carry a <datalist> of
// suggestions and the t010 Telegram-field keyboard-suppression attributes
// (t014, FR-5, FR-6) -- a <datalist> is not itself a field, so the "five
// fields" count above still holds. t017 (FR-9.2) adds the required
// member-category field immediately after the timezone field.
export default function AdminTripForm({
  trip,
  timezoneOptions,
  memberCategoryOptions,
  labels,
}: AdminTripFormProps) {
  const form = useForm<TripFormData>({
    title: trip.title,
    start_date: trip.startDate,
    end_date: trip.endDate,
    timezone: trip.timezone,
    member_category: trip.memberCategory,
    links: trip.links,
    authenticity_token: csrfToken(),
  })

  // The wire payload nests the five trip fields (plus links) under
  // "trip" so Trip::AdminTripController can read them with Rails 8's
  // params.expect(trip: [...]) -- a transform, not a change to the form's
  // own flat `data`/`errors` shape above (see that interface's comment).
  form.transform((data) => ({
    trip: {
      title: data.title,
      start_date: data.start_date,
      end_date: data.end_date,
      timezone: data.timezone,
      member_category: data.member_category,
      links: data.links,
    },
    authenticity_token: data.authenticity_token,
  }))

  // No `extraHint`: the trip form has no map.
  const linksLabels: LinksFieldsetLabels = {
    heading: labels.linksHeading,
    optional: labels.optional,
    rowHeading: labels.linksRowHeading,
    remove: labels.linksRemove,
    add: labels.linksAdd,
    labelLabel: labels.linksLabelLabel,
    urlLabel: labels.linksUrlLabel,
    hint: labels.linksHint,
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    form.patch("/admin/trip")
  }

  return (
    <AdminLayout>
      <Link href="/admin" className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {labels.back}
      </Link>
      <h2 className="text-[22px] font-bold leading-7 text-gray-900">{labels.heading}</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.titleLabel}</span>
          <input
            type="text"
            value={form.data.title}
            onChange={(event) => form.setData("title", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.titleHint}</span>
          {form.errors.title && <span className={ERROR_CLASSES}>{form.errors.title}</span>}
        </label>
        <div className="flex gap-3">
          <label className="flex flex-1 flex-col gap-1.5">
            <span className={LABEL_TEXT_CLASSES}>{labels.startDateLabel}</span>
            <input
              type="date"
              value={form.data.start_date}
              onChange={(event) => form.setData("start_date", event.target.value)}
              className={DATE_INPUT_CLASSES}
            />
            {form.errors.start_date && <span className={ERROR_CLASSES}>{form.errors.start_date}</span>}
          </label>
          <label className="flex flex-1 flex-col gap-1.5">
            <span className={LABEL_TEXT_CLASSES}>{labels.endDateLabel}</span>
            <input
              type="date"
              value={form.data.end_date}
              onChange={(event) => form.setData("end_date", event.target.value)}
              className={DATE_INPUT_CLASSES}
            />
            {form.errors.end_date && <span className={ERROR_CLASSES}>{form.errors.end_date}</span>}
          </label>
        </div>
        <p className={HINT_CLASSES}>{labels.datesHint}</p>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.timezoneLabel}</span>
          <input
            type="text"
            list={TIMEZONE_LIST_ID}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={form.data.timezone}
            onChange={(event) => form.setData("timezone", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.timezoneHint}</span>
          {form.errors.timezone && <span className={ERROR_CLASSES}>{form.errors.timezone}</span>}
        </label>
        <datalist id={TIMEZONE_LIST_ID}>
          {timezoneOptions.map((timezone) => (
            <option key={timezone} value={timezone} />
          ))}
        </datalist>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.memberCategoryLabel}</span>
          <input
            type="text"
            list={MEMBER_CATEGORY_LIST_ID}
            value={form.data.member_category}
            onChange={(event) => form.setData("member_category", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.memberCategoryHint}</span>
          {form.errors.member_category && (
            <span className={ERROR_CLASSES}>{form.errors.member_category}</span>
          )}
        </label>
        <datalist id={MEMBER_CATEGORY_LIST_ID}>
          {memberCategoryOptions.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>
        <LinksFieldset
          rows={form.data.links}
          onChange={(links) => form.setData("links", links)}
          labels={linksLabels}
          errors={form.errors as Record<string, string | undefined>}
        />
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
      </form>
    </AdminLayout>
  )
}
